import { Instagram, Mail, MessageCircle } from 'lucide-react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';

import { StaticPageLayout } from '@/components/layout/StaticPageLayout';
import { XIcon } from '@/components/ui/XIcon';

export function ContactPage() {
  const { t } = useTranslation();
  const pagesLoaded = useNamespace('pages');

  if (!pagesLoaded) {
    return null;
  }

  const contactMethods = [
    {
      icon: Mail,
      title: t('pages:contact.methods.email.title'),
      description: t('pages:contact.methods.email.description'),
      action: 'hello@splitfiesta.com',
      href: 'mailto:hello@splitfiesta.com',
    },
    {
      icon: Instagram,
      title: t('pages:contact.methods.instagram.title'),
      description: t('pages:contact.methods.instagram.description'),
      action: '@splitfiesta',
      href: 'https://instagram.com/splitfiesta',
      external: true,
    },
    {
      icon: XIcon,
      title: t('pages:contact.methods.x.title'),
      description: t('pages:contact.methods.x.description'),
      action: '@splitfiesta',
      href: 'https://x.com/splitfiesta',
      external: true,
    },
  ];

  return (
    <StaticPageLayout
      title={t('pages:contact.title')}
      subtitle={t('pages:contact.subtitle')}
    >
      <div className="space-y-8">
        {/* Contact Methods */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {contactMethods.map((method, index) => (
            <a
              key={index}
              href={method.href}
              target={method.external ? '_blank' : undefined}
              rel={method.external ? 'noopener noreferrer' : undefined}
              className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-orange-300 dark:hover:border-orange-700 hover:shadow-md transition-all text-center group"
            >
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <method.icon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                {method.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {method.description}
              </p>
              <span className="text-orange-600 dark:text-orange-400 font-medium">
                {method.action}
              </span>
            </a>
          ))}
        </div>

        {/* Response Time */}
        <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                {t('pages:contact.responseTime.title')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {t('pages:contact.responseTime.description')}
              </p>
            </div>
          </div>
        </div>

        {/* Bug Reports */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            {t('pages:contact.bugReports.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('pages:contact.bugReports.description')}
          </p>
          <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400 space-y-1">
            <li>{t('pages:contact.bugReports.items.describe')}</li>
            <li>{t('pages:contact.bugReports.items.steps')}</li>
            <li>{t('pages:contact.bugReports.items.device')}</li>
          </ul>
        </div>
      </div>
    </StaticPageLayout>
  );
}

export default ContactPage;
